import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import type { CurrentUser, Permission, SetupState } from '../api/types';

export interface AuthState {
  /// Null while the first setup-state and session lookup are still running.
  setup: SetupState | undefined;
  user: CurrentUser | undefined;
  loading: boolean;
  error: Error | undefined;
  signIn: (userName: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /// Applies a token the setup wizard just received, without a second round trip. It does not
  /// end the wizard - only refresh() does, once the wizard says it is finished.
  adopt: (token: string, user: CurrentUser) => void;
  refresh: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

export function useAuth(): AuthState {
  const state = useContext(AuthContext);

  if (!state) {
    throw new Error('useAuth was called outside of AuthProvider.');
  }

  return state;
}

/// The name of this server, but only when reading it is worth the reader's time. The logo
/// already says Emiro, so a server that goes by the product's own name is not named twice.
export function useDistinctServerName(): string | undefined {
  const { setup } = useAuth();
  const { t } = useTranslation();
  const name = setup?.serverName?.trim();

  return name && name.toLowerCase() !== t('app.name').toLowerCase() ? name : undefined;
}
